// Imported from the api slices rather than the feature barrels: the shell is
// always mounted, and the barrels would pull five screens' worth of UI with them.
import { useListPartnersQuery } from "@/features/partners/api/partnersApi";
import { useListEmployersQuery } from "@/features/employers/api/employersApi";
import { useListGroupsQuery } from "@/features/groups/api/groupsApi";
import { useListDivisionsQuery } from "@/features/divisions/api/divisionsApi";
import { useListLocationsQuery } from "@/features/locations/api/locationsApi";
import { useSeatScope } from "@/hooks/useSeatScope";
import { seatFloorLevel } from "@/lib/seatScope";
import { toIdNameMap } from "@/shared/lib/idName";

/**
 * Display name of the node the signed-in user is seated on — "Marpai Health"
 * rather than the merchant code the header pairs with the level label.
 *
 * The badge reads "<level>: <who>", so pairing "Employer" with the merchant
 * code named the wrong thing entirely: the tenant, not the employer signed in.
 *
 * Undefined for a platform seat (there is no node to name) and while the lookup
 * is in flight, so the caller keeps showing whatever it had. Only the query for
 * the caller's own level runs; the rest are skipped, and the answers come from
 * the same RTK Query cache the hierarchy pickers already fill.
 */
export function useSeatName(): string | undefined {
  const seat = useSeatScope();
  const level = seatFloorLevel(seat);

  const { data: clients } = useListPartnersQuery(undefined, { skip: level !== "client" });
  const { data: employers } = useListEmployersQuery(undefined, { skip: level !== "employer" });
  const { data: groups } = useListGroupsQuery(undefined, { skip: level !== "group" });
  const { data: divisions } = useListDivisionsQuery(undefined, { skip: level !== "division" });
  const { data: locations } = useListLocationsQuery(undefined, { skip: level !== "location" });

  switch (level) {
    case "client":
      return nameOf(clients, "clients", "clientId", seat.clientId);
    case "employer":
      return nameOf(employers, "employers", "employerId", seat.employerId);
    case "group":
      return nameOf(groups, "groups", "groupId", seat.groupId);
    case "division":
      return nameOf(divisions, "divisions", "divisionId", seat.divisionId);
    case "location":
      return nameOf(locations, "locations", "locationId", seat.locationId);
    default:
      return undefined;
  }
}

function nameOf(
  response: unknown,
  namedKey: string,
  idField: string,
  id: number | undefined
): string | undefined {
  if (id == null) return undefined;
  return toIdNameMap(response, namedKey, idField).get(id) || undefined;
}
